import React, { useEffect, useRef, useState } from "react";
import { Save, Megaphone, CheckCircle2, Clock, XCircle, CircleSlash, Monitor, Smartphone, Upload } from "lucide-react";
import { adminApi } from "../../lib/api";
import ImageUpload from "./ImageUpload";
import VideoUpload from "./VideoUpload";
import { toVtt, parseCaptions } from "../../lib/captions";
import { PromoCard } from "../HomePromoPopup";
import { toast } from "sonner";

const empty = { enabled: false, kind: "guide", ref_id: "", link: "", start_date: "", end_date: "", heading: "", body: "", button_text: "", image: "", video: "", video_poster: "", video_captions: "", video_transcript: "", video_autoplay: true, size: "md", exit_intent_enabled: false };

const fmt = (d) => d ? new Date(d + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : null;
const daysUntil = (d) => Math.ceil((new Date(d + "T00:00:00") - new Date(new Date().toDateString())) / 86400000);

function promoStatus(f) {
  if (!f.enabled) return { icon: CircleSlash, cls: "bg-slate-100 text-slate-500", label: "Off", note: "Popup is turned off." };
  const today = new Date(new Date().toDateString());
  const start = f.start_date ? new Date(f.start_date + "T00:00:00") : null;
  const end = f.end_date ? new Date(f.end_date + "T00:00:00") : null;
  const range = start || end ? `${fmt(f.start_date) || "always"} – ${fmt(f.end_date) || "no end"}` : "No schedule (always on)";
  if (start && today < start) {
    const n = daysUntil(f.start_date);
    return { icon: Clock, cls: "bg-amber-50 text-amber-600", label: `Starts in ${n} day${n === 1 ? "" : "s"}`, note: range };
  }
  if (end && today > end) return { icon: XCircle, cls: "bg-red-50 text-red-500", label: "Ended", note: range };
  return { icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-600", label: "Live now", note: range };
}

export default function PromoManager() {
  const [form, setForm] = useState(empty);
  const [guides, setGuides] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [device, setDevice] = useState("desktop");
  const captionRef = useRef(null);
  const [stats, setStats] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    adminApi.promo.get().then((d) => setForm({ ...empty, ...d })).catch(() => {});
    adminApi.promo.stats().then(setStats).catch(() => {});
    adminApi.guides.list().then(setGuides).catch(() => {});
    adminApi.blog.list().then((b) => setBlogs(b.filter((x) => x.published))).catch(() => {});
    adminApi.assessments.list().then((a) => setAssessments(a.filter((x) => x.enabled))).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, video_captions: toVtt(form.video_captions || "") };
      if (payload.kind === "video" && !payload.video) payload.enabled = false;
      await adminApi.promo.update(payload);
      setForm((f) => ({ ...f, ...payload }));
      toast.success("Promo saved");
    }
    catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  const needsItem = form.kind === "guide" || form.kind === "blog" || form.kind === "assessment";
  const isImage = form.kind === "image";
  const isVideo = form.kind === "video";

  const pickCaptionFile = async (e) => {
    const file = e.target.files?.[0];
    if (captionRef.current) captionRef.current.value = "";
    if (!file) return;
    const text = toVtt(await file.text());
    set("video_captions", text);
    toast.success(`Caption file loaded — ${parseCaptions(text).length} lines. Remember to save.`);
  };
  const options = form.kind === "guide" ? guides : form.kind === "blog" ? blogs : form.kind === "assessment" ? assessments : [];
  const optionValue = (o) => (form.kind === "assessment" ? o.slug : o.id);

  // Resolve exactly the way the backend does, so the preview shows what visitors
  // will see: the chosen item's own title/summary/image unless overridden here.
  const selected = options.find((o) => optionValue(o) === form.ref_id);
  const previewPromo = {
    ...form,
    heading: form.heading || selected?.title || "",
    body: form.body || selected?.summary || selected?.excerpt || selected?.subtitle || selected?.short || "",
    image: form.image || selected?.image || selected?.cover_image || "",
  };
  const PREVIEW_SCALE = 0.45;   // 1180px of mock desktop, shown inside the panel
  const MOBILE = { w: 390, h: 780, scale: 0.62 };   // iPhone-ish viewport
  const status = promoStatus(form);

  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <Megaphone className="text-orange" size={22} />
        <h1 className="font-display font-extrabold text-2xl text-navy">Homepage Promo Popup</h1>
      </div>
      <p className="text-slate-500 text-sm mb-6">Promote one thing on the homepage. It appears shortly after the page loads and stays until the visitor closes it (shown once per session). Only one promo can run at a time, and it only appears on the homepage.</p>

      <div data-testid="promo-status" className={`max-w-2xl flex items-center gap-3 rounded-2xl px-5 py-3 mb-4 ${status.cls}`}>
        <status.icon size={20} className="shrink-0" />
        <div className="min-w-0">
          <p className="font-bold text-sm leading-tight">{status.label}</p>
          <p className="text-xs opacity-80">{status.note}</p>
        </div>
      </div>

      {stats && (
        <div data-testid="promo-perf" className="max-w-2xl grid grid-cols-3 gap-3 mb-6">
          <div className="rounded-2xl bg-white border border-slate-100 p-4 text-center shadow-sm">
            <p className="text-2xl font-display font-extrabold text-navy" data-testid="promo-impressions">{stats.impressions}</p>
            <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide mt-1">Views</p>
          </div>
          <div className="rounded-2xl bg-white border border-slate-100 p-4 text-center shadow-sm">
            <p className="text-2xl font-display font-extrabold text-navy" data-testid="promo-clicks">{stats.clicks}</p>
            <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide mt-1">Clicks</p>
          </div>
          <div className="rounded-2xl bg-white border border-slate-100 p-4 text-center shadow-sm">
            <p className="text-2xl font-display font-extrabold text-orange" data-testid="promo-ctr">{stats.ctr}%</p>
            <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide mt-1">Click rate</p>
          </div>
        </div>
      )}

      {stats?.video?.plays > 0 && (
        <div data-testid="promo-video-perf" className="max-w-2xl bg-white rounded-2xl border border-slate-100 p-6 shadow-sm mb-6">
          <h3 className="font-display font-bold text-navy">How far they watch</h3>
          <p className="text-xs text-slate-500 mt-1 leading-relaxed">
            Measured when the popup closes, so it shows where people stop rather than who pressed play.
            {stats.video.clip_seconds > 0 && ` The clip runs ${stats.video.clip_seconds}s.`}
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
            <div className="rounded-xl bg-slate-50 p-3 text-center">
              <p className="text-xl font-display font-extrabold text-navy" data-testid="promo-video-plays">{stats.video.plays}</p>
              <p className="text-[11px] text-slate-400 font-semibold uppercase tracking-wide mt-0.5">Watched</p>
            </div>
            <div className="rounded-xl bg-slate-50 p-3 text-center">
              <p className="text-xl font-display font-extrabold text-navy" data-testid="promo-video-avg">{stats.video.avg_percent}%</p>
              <p className="text-[11px] text-slate-400 font-semibold uppercase tracking-wide mt-0.5">Average watched</p>
              <p className="text-[11px] text-slate-400">{stats.video.avg_seconds}s</p>
            </div>
            <div className="rounded-xl bg-slate-50 p-3 text-center">
              <p className="text-xl font-display font-extrabold text-orange" data-testid="promo-video-finished">{stats.video.finished_rate}%</p>
              <p className="text-[11px] text-slate-400 font-semibold uppercase tracking-wide mt-0.5">Watched to the end</p>
              <p className="text-[11px] text-slate-400">{stats.video.halfway_rate}% past halfway</p>
            </div>
            <div className="rounded-xl bg-slate-50 p-3 text-center">
              <p className="text-xl font-display font-extrabold text-navy" data-testid="promo-video-sound">{stats.video.sound_on}</p>
              <p className="text-[11px] text-slate-400 font-semibold uppercase tracking-wide mt-0.5">Turned sound on</p>
            </div>
          </div>

          <div className="mt-5 space-y-2" data-testid="promo-video-buckets">
            {stats.video.buckets.map((b) => (
              <div key={b.label} className="flex items-center gap-3">
                <span className="text-xs text-slate-500 w-44 shrink-0">{b.label}</span>
                <div className="flex-1 h-2.5 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full bg-navy rounded-full" style={{ width: `${Math.round((b.count / stats.video.plays) * 100)}%` }} />
                </div>
                <span className="text-xs font-bold text-navy w-10 text-right">{b.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="max-w-2xl mb-6 bg-white rounded-2xl border border-slate-100 p-6 shadow-sm" data-testid="exit-intent-card">
        <label className="flex items-start gap-3 cursor-pointer">
          <input data-testid="promo-exit-intent" type="checkbox" checked={form.exit_intent_enabled} onChange={(e) => set("exit_intent_enabled", e.target.checked)} className="w-5 h-5 mt-0.5 accent-orange" />
          <span>
            <span className="font-semibold text-navy block">Show the "Let's Talk" exit-intent popup</span>
            <span className="text-sm text-slate-500 block mt-1">A gentle "Let's talk about your business" popup that appears once per visit when someone looks like they're about to leave the site (or after a while browsing). It's separate from the homepage promo below. Off by default.</span>
          </span>
        </label>
        <button data-testid="exit-intent-save-btn" onClick={save} disabled={saving} className="mt-4 inline-flex items-center gap-2 bg-orange hover:bg-orange-dark disabled:opacity-60 text-white font-bold px-5 py-2 rounded-full transition-colors text-sm">
          <Save size={15} /> {saving ? "Saving..." : "Save"}
        </button>
      </div>

      <div className="max-w-2xl space-y-6 bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
        <label className="flex items-center gap-3 cursor-pointer">
          <input data-testid="promo-enabled" type="checkbox" checked={form.enabled} onChange={(e) => set("enabled", e.target.checked)} className="w-5 h-5 accent-orange" />
          <span className="font-semibold text-navy">Show the promo popup on the homepage</span>
        </label>

        <div className={form.enabled ? "" : "opacity-50 pointer-events-none"}>
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4 pb-2 border-b border-slate-100">
              <Field label="Start date (optional)">
                <input data-testid="promo-start-date" type="date" value={form.start_date || ""} onChange={(e) => set("start_date", e.target.value)} className={inputCls} />
                <p className="text-xs text-slate-400 mt-1">Popup stays hidden before this date.</p>
              </Field>
              <Field label="End date (optional)">
                <input data-testid="promo-end-date" type="date" value={form.end_date || ""} min={form.start_date || undefined} onChange={(e) => set("end_date", e.target.value)} className={inputCls} />
                <p className="text-xs text-slate-400 mt-1">Auto-hides after this date (inclusive).</p>
              </Field>
            </div>
            <div className="pt-1">
              <label className="block text-sm font-semibold text-navy mb-2">Popup size</label>
              <div className="flex flex-wrap gap-2">
                {[["md", "Standard"], ["lg", "Large"], ["xl", "Extra large"], ["full", "Maximum"]].map(([k, label]) => (
                  <button key={k} type="button" data-testid={`promo-size-${k}`} onClick={() => set("size", k)}
                    className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${(form.size || "md") === k ? "bg-orange text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{label}</button>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-2">
                Standard is the original size and the smallest. Larger sizes give a promotional image far more room —
                useful for a poster or an event flyer. On phones every size fills the screen width, so this only
                changes how it looks on laptops and desktops.
              </p>

              {/* Live preview — the real popup component, scaled down */}
              <div className="mt-4" data-testid="promo-preview">
                <div className="relative z-10 flex items-center justify-between gap-3 mb-2 flex-wrap">
                  <p className="text-sm font-semibold text-navy">Live preview</p>
                  <div className="flex items-center gap-3">
                    <div className="flex bg-slate-100 rounded-full p-1">
                      {[["desktop", "Desktop", Monitor], ["mobile", "Mobile", Smartphone]].map(([k, label, Icon]) => (
                        <button key={k} type="button" data-testid={`promo-preview-${k}`} onClick={() => setDevice(k)}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${device === k ? "bg-white text-navy shadow-sm" : "text-slate-500 hover:text-navy"}`}>
                          <Icon size={13} /> {label}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-slate-400">
                      {device === "mobile" ? `Mobile, ${Math.round(MOBILE.scale * 100)}%` : `Desktop, ${Math.round(PREVIEW_SCALE * 100)}%`} of actual size
                    </p>
                  </div>
                </div>
                {device === "mobile" ? (
                  <div
                    className="rounded-[1.75rem] border-4 border-slate-800 overflow-hidden bg-slate-900 mx-auto"
                    style={{ width: MOBILE.w * MOBILE.scale, height: MOBILE.h * MOBILE.scale }}
                  >
                    <div
                      className="bg-navy-deep/90 flex items-center justify-center p-4"
                      style={{ width: MOBILE.w, height: MOBILE.h, transform: `scale(${MOBILE.scale})`, transformOrigin: "top left" }}
                    >
                      <PromoCard promo={previewPromo} preview mobile />
                    </div>
                  </div>
                ) : (
                  <div className="rounded-2xl border border-navy/10 overflow-hidden bg-slate-900" style={{ height: 760 * PREVIEW_SCALE }}>
                    <div
                      className="bg-navy-deep/90 flex items-center justify-center p-4"
                      style={{ width: `${100 / PREVIEW_SCALE}%`, height: 760, transform: `scale(${PREVIEW_SCALE})`, transformOrigin: "top left" }}
                    >
                      <PromoCard promo={previewPromo} preview />
                    </div>
                  </div>
                )}
                <p className="text-xs text-slate-400 mt-2">
                  Updates as you type. Nothing here is saved until you press Save.
                  {device === "mobile" && " On a phone the popup fills the screen width, so all four sizes look the same here — that's expected."}
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-navy mb-2">What to promote</label>
              <div className="flex flex-wrap gap-2">
                {[["guide", "A Guide"], ["blog", "A Blog Post"], ["assessment", "An Assessment Tool"], ["meeting_room", "Meeting Room Hire"], ["image", "Custom Image"], ["video", "Custom Video"]].map(([k, label]) => (
                  <button key={k} type="button" data-testid={`promo-kind-${k}`} onClick={() => { set("kind", k); set("ref_id", ""); }}
                    className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${form.kind === k ? "bg-orange text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{label}</button>
                ))}
              </div>
            </div>

            {needsItem && (
              <div>
                <label className="block text-sm font-semibold text-navy mb-1.5">{form.kind === "guide" ? "Choose a guide" : form.kind === "blog" ? "Choose a blog post" : "Choose an assessment tool"}</label>
                <select data-testid="promo-ref" value={form.ref_id} onChange={(e) => set("ref_id", e.target.value)} className={inputCls}>
                  <option value="">— Select —</option>
                  {options.map((o) => <option key={optionValue(o)} value={optionValue(o)}>{o.title}</option>)}
                </select>
                {form.enabled && !form.ref_id && <p className="text-xs text-red-500 mt-1">Pick an item to promote.</p>}
              </div>
            )}

            {isVideo && (
              <div className="space-y-4 rounded-xl bg-slate-50 border border-slate-100 p-4" data-testid="promo-video-fields">
                <p className="text-sm text-slate-500">Upload a video promo — an avatar clip, an event trailer or a short welcome. It starts muted with captions showing, and a visitor can tap for sound, replay it, or click the clip to follow the link.</p>
                <VideoUpload label="Promo video (required)" value={form.video || ""} onChange={(v) => set("video", v)} hint="Keep it under about 45 seconds. Max 300MB." />
                <ImageUpload label="Poster image (optional)" value={form.video_poster || ""} onChange={(v) => set("video_poster", v)} hint="Shown before playback. Leave blank to use the first frame." />
                <Field label="Link when clicked">
                  <input data-testid="promo-video-link" value={form.link} onChange={(e) => set("link", e.target.value)} className={inputCls} placeholder="/services/payroll  or  /contact  or  https://..." />
                  <p className="text-xs text-slate-400 mt-1">Used by both the button and the clip itself.</p>
                </Field>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input data-testid="promo-video-autoplay" type="checkbox" checked={form.video_autoplay !== false} onChange={(e) => set("video_autoplay", e.target.checked)} className="w-5 h-5 accent-orange" />
                  <span className="text-sm text-navy font-semibold">Start playing automatically (muted)</span>
                </label>
                {form.enabled && !form.video && <p className="text-xs text-red-500">Upload a video to use this option.</p>}
              </div>
            )}

            {isImage && (
              <div className="space-y-4 rounded-xl bg-slate-50 border border-slate-100 p-4">
                <p className="text-sm text-slate-500">Upload an image (e.g. a promotional banner). The whole popup image is clickable and sends visitors to the link you choose.</p>
                <ImageUpload label="Promo image (required)" value={form.image || ""} onChange={(v) => set("image", v)} />
                <Field label="Link when clicked">
                  <input data-testid="promo-link" value={form.link} onChange={(e) => set("link", e.target.value)} className={inputCls} placeholder="/services/payroll  or  /contact  or  https://..." />
                  <p className="text-xs text-slate-400 mt-1">Use an internal path (e.g. <code>/pricing</code>, <code>/meeting-room-hire</code>) or a full URL.</p>
                </Field>
                {form.enabled && !form.image && <p className="text-xs text-red-500">Upload an image to use this option.</p>}
              </div>
            )}

            {!isImage && (
            <div className="pt-2 border-t border-slate-100">
              <p className="text-sm font-semibold text-navy mb-1">Optional overrides</p>
              <p className="text-xs text-slate-400 mb-4">Leave blank to auto-fill from the selected item.</p>
              <div className="space-y-4">
                <Field label="Heading"><input data-testid="promo-heading" value={form.heading} onChange={(e) => set("heading", e.target.value)} className={inputCls} /></Field>
                <Field label="Body text"><textarea data-testid="promo-body" rows={2} value={form.body} onChange={(e) => set("body", e.target.value)} className={inputCls} /></Field>
                <Field label="Button text"><input data-testid="promo-button" value={form.button_text} onChange={(e) => set("button_text", e.target.value)} className={inputCls} placeholder={form.kind === "guide" ? "Free Download" : form.kind === "blog" ? "Read the article" : "Find out more"} /></Field>
                {!isVideo && <ImageUpload label="Image (optional)" value={form.image || ""} onChange={(v) => set("image", v)} />}
                {!isVideo && (
                  <VideoUpload
                    label="Video (optional)"
                    value={form.video || ""}
                    onChange={(v) => set("video", v)}
                    hint="Plays instead of the image. Starts muted with a tap for sound."
                  />
                )}
                {!!form.video && (
                  <div className="rounded-xl bg-slate-50 border border-slate-100 p-4 space-y-3" data-testid="promo-video-captions-block">
                    <div className="flex flex-wrap items-center gap-3">
                      <input ref={captionRef} type="file" accept=".vtt,.srt,text/vtt,text/plain" onChange={pickCaptionFile} className="hidden" data-testid="promo-caption-file" />
                      <button type="button" onClick={() => captionRef.current?.click()} data-testid="promo-caption-upload" className="inline-flex items-center gap-1.5 bg-white border border-slate-200 hover:border-orange text-navy text-xs font-bold px-3.5 py-2 rounded-full transition-colors">
                        <Upload size={13} /> Upload caption file (.vtt / .srt)
                      </button>
                      <span className="text-xs text-slate-400">{parseCaptions(form.video_captions).length} caption line{parseCaptions(form.video_captions).length === 1 ? "" : "s"} — optional</span>
                    </div>
                    <Field label="Captions (WebVTT or SRT)">
                      <textarea data-testid="promo-video-captions" rows={5} value={form.video_captions || ""} onChange={(e) => set("video_captions", e.target.value)} placeholder={"WEBVTT\n\n00:00:00.000 --> 00:00:03.500\nSomething worth saying."} className={`${inputCls} font-mono text-xs`} />
                    </Field>
                    <Field label="Transcript (optional, read by screen readers and Google)">
                      <textarea data-testid="promo-video-transcript" rows={3} value={form.video_transcript || ""} onChange={(e) => set("video_transcript", e.target.value)} className={inputCls} />
                    </Field>
                  </div>
                )}
              </div>
            </div>
            )}
          </div>
        </div>

        <button data-testid="promo-save-btn" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark disabled:opacity-60 text-white font-bold px-6 py-2.5 rounded-full transition-colors">
          <Save size={16} /> {saving ? "Saving..." : "Save promo"}
        </button>
      </div>
    </div>
  );
}

const inputCls = "w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange/40";
const Field = ({ label, children }) => (
  <div>
    <label className="block text-sm font-semibold text-navy mb-1.5">{label}</label>
    {children}
  </div>
);
